import { useLayoutEffect, SyntheticEvent } from 'react';

export const stopEventEffects = (event: Event | SyntheticEvent): void => {
  event.preventDefault();
  event.stopPropagation();
};

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const mod = (
  a: number,
  q: number,
  p?: number,
  round?: 'up' | 'down'
): number => {
  let r = a % q;

  if (p !== undefined) {
    const m = 10 ** p;

    if (round === 'up') r = Math.ceil(r * m) / m;
    else if (round === 'down') r = Math.floor(r * m) / m;
    r = parseFloat(r.toFixed(p));
  }

  return (r + q) % q;
};

export const getModDistance = (a: number, b: number, q: number): number => {
  const average = (a + b) / 2;
  const standard = Math.abs(a - b);
  const reverse = Math.abs(mod(a - average, q) - mod(b - average, q));

  return Math.min(standard, reverse);
};

export const shuffleArray = (array: any[]): any[] =>
  array
    .map((value) => ({ value, compare: Math.random() }))
    .sort((a, b) => a.compare - b.compare)
    .map(({ value }) => value);

export const isObject = (value: any): boolean =>
  typeof value === 'object' && !Array.isArray(value) && value !== null;

export const isEmptyObject = (value: any): boolean => {
  if (isObject(value)) {
    for (const k in value) // eslint-disable-line no-restricted-syntax
      if (Object.prototype.hasOwnProperty.call(value, k)) return false;

    return true;
  }

  return false;
};

export const clone = <T>(source: T): T => {
  if (source instanceof Date) return new Date(source.getTime()) as unknown as T;
  else if (Array.isArray(source))
    return source.map((v) => clone(v)) as unknown as T;
  else if (isObject(source))
    return Object.keys(source).reduce(
      (acc, k) => ({ ...acc, [k]: clone(source[k]) }),
      {} as T
    );

  return source;
};

export const useTitle = (title: string): void => {
  useLayoutEffect(() => {
    const prevTitle = document.title;

    document.title = title;

    return () => {
      document.title = prevTitle;
    };
  });
};
